import { useEffect, useState } from 'react';
import { endpoints } from '@api/client';

/**
 * Deep-merge a stored content value over built-in page defaults.
 *
 * Objects are merged key-by-key; arrays and primitives from the stored value
 * replace the default wholesale (admins manage whole lists like dates/photos).
 */
function deepMerge(defaults, override) {
  if (override === null || override === undefined) return defaults;
  if (Array.isArray(override) || typeof override !== 'object') return override;
  if (typeof defaults !== 'object' || defaults === null || Array.isArray(defaults)) {
    return override;
  }
  const out = { ...defaults };
  for (const key of Object.keys(override)) {
    out[key] = deepMerge(defaults[key], override[key]);
  }
  return out;
}

/**
 * Fetch an admin-editable content block and merge it over `defaults`.
 *
 * The page renders identically to its hardcoded version until an admin saves
 * a customisation, so the public layout/flow never changes unexpectedly.
 *
 * @param {string} key       block slug (cfp | registration | gallery | digital)
 * @param {object} defaults  built-in fallback content
 * @returns {{ content: object, loading: boolean, ready: boolean }}
 */
export function usePageContent(key, defaults) {
  const [content, setContent] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    endpoints
      .contentBlock(key)
      .then((res) => {
        if (!active) return;
        const value = res?.data?.data?.value;
        setContent(value ? deepMerge(defaults, value) : defaults);
      })
      .catch(() => {
        if (active) setContent(defaults);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        setReady(true);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { content, loading, ready };
}
